<script lang="ts" generic="T extends Object" setup>
import type { ZodSchema } from "zod";
import { useEventBus } from "@vueuse/core";

export interface ValidateReturnSchema<T> {
  result: T;
  errors: IFormError[] | null;
}

class FormException extends Error {
  constructor(message: string) {
    super(message);
    this.message = message;
    Object.setPrototypeOf(this, FormException.prototype);
  }
}

const emit = defineEmits<{
  (event: "submit", state: T): void;
  (event: "error", errors: IFormError[]): void;
}>();

const props = withDefaults(
  defineProps<{
    state: T;
    schema?: ZodSchema;
    validate?: ((state: T) => Promise<IFormError[]>) | ((state: T) => IFormError[]);
    validateOn?: TFormEventType[];
  }>(),
  {
    validate: () => [],
    validateOn: () => ["blur", "input", "change", "submit"],
  }
);

const parsedValue = ref<T | null>(null);

const bus = useEventBus<IFormEvent>("form-bus");
const toast = useToast();

bus.on(async (event) => {
  if (event.type !== "submit" && props.validateOn?.includes(event.type)) {
    await validateForm(event.path, { silent: true });
  }
});

const errors = ref<IFormError[]>([]);

provide("form-errors", errors);
provide("form-events", bus);

async function getErrors(): Promise<IFormError[]> {
  let errs = await props.validate(props.state);

  if (props.schema) {
    const { errors, result } = await validateSchema(props.state, props.schema as unknown as ZodSchema);
    if (errors) {
      errs = errs.concat(errors);
      errs?.forEach((e) => {
        e?.path === "toast" && toast.error(e.message);
      });
    } else {
      parsedValue.value = result;
    }
  }

  return errs;
}

async function validateForm(path?: string, opts: { silent?: boolean } = { silent: false }): Promise<T> {
  if (path) {
    const otherErrors = errors.value.filter((error) => error.path !== path);
    const pathErrors = (await getErrors()).filter((error) => error.path === path);
    errors.value = otherErrors.concat(pathErrors);
  } else {
    errors.value = await getErrors();
  }

  if (!opts.silent && errors.value.length > 0) {
    throw new FormException(`Form validation failed: ${JSON.stringify(errors.value, null, 2)}`);
  }
  return props.state;
}

async function validateSchema(state: any, schema: ZodSchema): Promise<ValidateReturnSchema<typeof state>> {
  const result = await schema.safeParseAsync(state);
  if (result.success === false) {
    const errors = result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    }));

    return {
      errors,
      result: null,
    };
  }
  return {
    result: result.data,
    errors: null,
  };
}

async function onSubmit(): Promise<void> {
  try {
    if (props.validateOn?.includes("submit")) await validateForm();

    emit("submit", props.schema ? parsedValue.value : props.state);
  } catch (error) {
    if (!(error instanceof FormException)) {
      throw error;
    }

    emit("error", errors.value);
  }
}

onMounted(() => {
  if (props.validateOn?.includes("mounted")) validateForm(undefined, { silent: true });
});

defineExpose({
  /**
   * Triggers form validation. Will raise any errors unless `opts.silent` is set to true.
   */
  validate: validateForm,
  /**
   * A reference to the array containing validation errors. Use this to access or manipulate the error information.
   */
  errors,
  /**
   * Sets form errors for a given path. If no path is provided, overrides all errors.
   */
  setErrors(errs: IFormError[], path?: string) {
    errors.value = errs;
    if (path) {
      errors.value = errors.value.filter((error) => error.path !== path).concat(errs);
    } else {
      errors.value = errs;
    }
  },
  /**
   * Retrieves form errors associated with a specific path. If no path is provided, returns all form errors.
   */
  getErrors(path?: string) {
    if (path) return errors.value.filter((err) => err.path === path);

    return errors.value;
  },
  /**
   * Clears form errors associated with a specific path. If no path is provided, clears all form errors.
   */
  clear(path?: string) {
    if (path) {
      errors.value = errors.value.filter((err) => err.path === path);
    } else {
      errors.value = [];
    }
  },
} as IForm<T>);
</script>

<template>
  <form @submit.prevent="onSubmit">
    <slot />
  </form>
</template>
